import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Trash2, Paperclip, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { SenderAvatar } from "./MailAvatar";
import { InlineReply } from "./InlineReply";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const who = (a) => (a?.name ? `${a.name} <${a.email}>` : a?.email || "");
const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const Body = ({ full }) => {
  const ref = useRef(null);
  const resize = () => {
    const f = ref.current;
    if (f?.contentDocument) f.style.height = Math.min(Math.max(f.contentDocument.documentElement.scrollHeight + 24, 80), 3000) + "px";
  };
  if (full.html) {
    return (
      <iframe
        ref={ref}
        title="Email content"
        sandbox="allow-same-origin allow-popups"
        onLoad={resize}
        srcDoc={`<!DOCTYPE html><html><head><style>body{font-family:"Segoe UI",Arial,sans-serif;font-size:14px;color:#242424;margin:0;padding:4px;}img{max-width:100%;height:auto;}a{color:#0F6CBD;}</style></head><body>${full.html}</body></html>`}
        className="w-full bg-white border-0"
        style={{ minHeight: 80 }}
        data-testid="mail-html-frame"
      />
    );
  }
  return <pre className="whitespace-pre-wrap break-words text-[14px] text-ol-text leading-relaxed" style={{ fontFamily: '"Segoe UI", Arial, sans-serif' }}>{full.text || "(empty message)"}</pre>;
};

const Card = ({ api, folder, summary, defaultOpen, activeEmail, onForward, onSent, onSeen, onUndoableSend }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !full) {
      setLoading(true);
      api.get(`/mail/message/${summary.uid}`, { params: { folder } })
        .then(({ data }) => {
          setFull(data);
          if (!summary.seen) { api.post(`/mail/message/${summary.uid}/seen`, { seen: true }, { params: { folder } }).catch(() => {}); onSeen?.(summary.uid); }
        })
        .catch(() => toast.error("Could not open this message"))
        .finally(() => setLoading(false));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (att) => {
    try {
      const res = await api.get(`/mail/message/${summary.uid}/attachment/${att.index}`, { params: { folder }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download attachment"); }
  };

  return (
    <div className="border border-ol-line rounded-[6px] bg-white overflow-hidden" data-testid={`conv-card-${summary.uid}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-ol-hover transition-colors" data-testid={`conv-card-toggle-${summary.uid}`}>
        <SenderAvatar email={summary.from?.email} name={summary.from?.name} size={30} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`truncate text-[13px] ${summary.seen ? "text-ol-text" : "text-ol-text font-bold"}`}>{summary.from?.name || summary.from?.email}</span>
            <span className="text-[11px] text-ol-sub shrink-0">{fmtDate(summary.date)}</span>
          </div>
          {!open && <p className="truncate text-[12px] text-ol-sub">{summary.subject}</p>}
        </div>
        {open ? <ChevronDown size={16} className="text-ol-sub shrink-0" /> : <ChevronRight size={16} className="text-ol-sub shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-ol-line pt-3">
          <p className="text-[12px] text-ol-sub mb-2">To: {(full?.to || []).map(who).join(", ") || "—"}{full?.cc?.length ? ` · Cc: ${full.cc.map(who).join(", ")}` : ""}</p>
          {loading ? (
            <p className="text-[13px] text-ol-sub py-4">Loading…</p>
          ) : (
            <>
              {full?.attachments?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {full.attachments.map((att) => (
                    <button key={att.index} onClick={() => download(att)} className="flex items-center gap-2 text-[12px] text-ol-text border border-ol-line hover:border-ol-blue bg-ol-bg px-3 py-2 rounded-[4px] transition-colors" data-testid={`conv-attachment-${att.index}`}>
                      <Paperclip size={14} className="text-ol-blue" />{att.filename}<span className="text-ol-sub">{fmtSize(att.size)}</span><Download size={13} className="text-ol-sub" />
                    </button>
                  ))}
                </div>
              )}
              {full && <Body full={full} />}
              {full && defaultOpen && (
                <InlineReply api={api} message={full} activeEmail={activeEmail} onForward={onForward} onSent={onSent} onUndoableSend={onUndoableSend} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const ConversationView = ({ api, folder, thread, activeEmail, onBack, onForward, onDeleteLatest, onSeen, onSent, onUndoableSend }) => {
  // thread.messages is newest-first; show newest at top, expanded.
  const latest = thread.messages[0];
  return (
    <div className="flex flex-col h-full" data-testid="mail-conversation-view">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-ol-line shrink-0">
        <button onClick={onBack} className="md:hidden flex items-center gap-1.5 text-[13px] text-ol-sub hover:text-ol-text pr-2" data-testid="conv-back-button"><ArrowLeft size={16} /> Back</button>
        <h2 className="text-[19px] font-semibold text-ol-text leading-snug truncate flex-1" data-testid="conv-subject">{latest.subject}</h2>
        <span className="text-[12px] text-ol-sub shrink-0" data-testid="conv-count">{thread.count} messages</span>
        <button onClick={onDeleteLatest} className="p-2 text-ol-sub hover:bg-ol-hover hover:text-red-500 rounded-[4px] transition-colors" title="Delete latest" data-testid="conv-delete-latest"><Trash2 size={16} /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 flex flex-col gap-3 bg-ol-bg">
        {thread.messages.map((m, i) => (
          <Card key={m.uid} api={api} folder={folder} summary={m} defaultOpen={i === 0} activeEmail={activeEmail} onForward={onForward} onSent={onSent} onSeen={onSeen} onUndoableSend={onUndoableSend} />
        ))}
      </div>
    </div>
  );
};
